/**
 * Konkord: games inside a call, without a game server. The app of one player
 * (the host, normally whoever started the game) applies the moves and sends
 * the game to everyone in the call over LiveKit data messages; the others
 * watch, or ask the host to play when they may. Every player keeps a copy on
 * the device and in their Stoat Saved Notes, so a game survives anyone
 * dropping out and can be continued later.
 */
import { type Accessor, batch, createSignal, untrack } from "solid-js";

import {
  ConnectionState,
  type Participant,
  type Room,
  RoomEvent,
} from "livekit-client";
import type { Channel, Client } from "stoat.js";
import { ulid } from "ulid";

import {
  type Difficulty,
  type Minesweeper,
  type MinesweeperMove,
  OPEN,
  newMinesweeper,
  play,
  setRunning,
} from "./minesweeper";
import {
  type GameMode,
  type GameRecord,
  MAX_PLAYERS,
  SAVE_PREFIX,
  canPlay,
  decodeGame,
  encodeGame,
} from "./record";

const TOPIC = "konkord.game";
const LOCAL_KEY = "konkord.games.v1";
/** games their owner ended: their states and saves are ignored from then on */
const CLOSED_KEY = "konkord.games.closed.v1";
const CLOSED_MAX = 200;
const LOCAL_SAVE_MS = 500;
/** Saved Notes are updated at most this often while playing */
const NOTES_SAVE_MS = 60_000;
/** Saved games nobody touched for this long are dropped */
const SAVED_MAX_AGE_MS = 30 * 24 * 60 * 60_000;
const SAVED_MAX = 30;
/** A player other than the owner may take over a game after its host left this long ago */
export const TAKE_OVER_AFTER_MS = 15_000;
/** "Someone started a game" stays this long */
const ANNOUNCE_MS = 8000;
/** A request to play can be sent again after this long */
export const REQUEST_AGAIN_MS = 30_000;
/** "Your request was declined" stays this long */
const DECLINED_MS = 6000;
const MAX_REQUESTS = 10;
/** Turns: a move takes at most this long, then the next player goes */
export const TURN_MS = 30_000;

const opened = (board: Minesweeper) =>
  board.cells.reduce((sum, cell) => sum + (cell & OPEN ? 1 : 0), 0);

export type GameAction =
  | { type: "move"; move: MinesweeperMove }
  | { type: "allow"; user: string; allow: boolean }
  | { type: "pause"; paused: boolean }
  /** a player stops playing (back to watching); the owner hands the game on */
  | { type: "leave" }
  /** someone who played before plays again */
  | { type: "rejoin" };

type Envelope =
  | { t: "state"; game: string; now: number }
  | { t: "ask"; id: string; action: GameAction }
  | { t: "close"; id: string }
  | { t: "request"; id: string }
  | { t: "answer"; id: string; ok: boolean };

const finished = (game: GameRecord) =>
  game.board.status === "won" || game.board.status === "lost";

/**
 * Whether `game` replaces `known`: a later takeover, else a later change. Two
 * hosts at the same point (takeovers at once): the smaller id wins on both sides
 */
export function supersedes(game: GameRecord, known: GameRecord) {
  if (game.epoch !== known.epoch) return game.epoch > known.epoch;
  if (game.seq !== known.seq) return game.seq > known.seq;
  return game.host !== known.host && game.host < known.host;
}

/**
 * Stop the clock of a game whose host is gone, counting play time up to `at`
 */
function stopClock(game: GameRecord, at = game.updatedAt): GameRecord {
  const { board } = game;
  if (board.since === undefined) return game;
  return {
    ...game,
    board: {
      ...board,
      elapsed: board.elapsed + Math.max(0, at - board.since),
      since: undefined,
    },
  };
}

function readStorage<T>(key: string, fallback: T): T {
  try {
    const value = JSON.parse(localStorage.getItem(key) ?? "null");
    return value !== null && typeof value === typeof fallback ? value : fallback;
  } catch {
    return fallback;
  }
}

function writeStorage(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage full or blocked: the Saved Notes copy still exists
  }
}

const readLocal = () => readStorage<Record<string, string>>(LOCAL_KEY, {});

export class CallGames {
  /** games running in this call, by id */
  games: Accessor<Record<string, GameRecord>>;
  #setGames: (games: Record<string, GameRecord>) => void;
  /** our saved games of this call's channel that nobody runs now */
  saved: Accessor<GameRecord[]>;
  #setSaved: (games: GameRecord[]) => void;
  /** people in the call (to know whose game is waiting for its host) */
  present: Accessor<ReadonlySet<string>>;
  #setPresent: (present: ReadonlySet<string>) => void;
  /** when the host of a game left the call */
  away: Accessor<Record<string, number>>;
  #setAway: (away: Record<string, number>) => void;

  /** someone just started a game */
  announcement: Accessor<{ id: string; from: string } | undefined>;
  #setAnnouncement: (value: { id: string; from: string } | undefined) => void;
  #announceTimer?: ReturnType<typeof setTimeout>;
  /** people asking to play our games, by game */
  requests: Accessor<Record<string, string[]>>;
  #setRequests: (requests: Record<string, string[]>) => void;
  /** when we asked to play each game */
  requested: Accessor<Record<string, number>>;
  #setRequested: (requested: Record<string, number>) => void;
  /** someone let us play a game we didn't ask for: an invitation */
  invitation: Accessor<{ id: string; from: string } | undefined>;
  #setInvitation: (value: { id: string; from: string } | undefined) => void;
  /** when the owner declined our request, by game */
  declined: Accessor<Record<string, number>>;
  #setDeclined: (declined: Record<string, number>) => void;

  /** the games screen is shown in the call */
  open: Accessor<boolean>;
  setOpen: (open: boolean) => void;
  /** game shown on the games screen (none: the list) */
  selected: Accessor<string | undefined>;
  select: (id: string | undefined) => void;

  #room?: Room;
  #channel = "";
  #me = "";
  #client?: () => Client;
  #unbind?: () => void;
  #localTimer?: ReturnType<typeof setTimeout>;
  #notesTimer?: ReturnType<typeof setInterval>;
  /** games waiting to be written to the device */
  #pendingLocal = new Map<string, GameRecord>();
  /** games whose Saved Notes copy is behind, as they were when marked */
  #dirty = new Map<string, GameRecord>();
  /** Saved Notes writes run one after the other */
  #writing: Promise<void> = Promise.resolve();
  /** game id -> our Saved Notes message holding it */
  #noteIds = new Map<string, string>();
  #notesChannel?: Promise<Channel | undefined>;
  /** games whose saves were dropped (ended or finished) */
  #gone = new Set<string>();
  #closed = new Set<string>(readStorage<string[]>(CLOSED_KEY, []));
  /** turns: moves the game on when a player takes too long (we run it) */
  #turnTimers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor() {
    const [games, setGames] = createSignal<Record<string, GameRecord>>({});
    this.games = games;
    this.#setGames = setGames;
    const [saved, setSaved] = createSignal<GameRecord[]>([]);
    this.saved = saved;
    this.#setSaved = setSaved;
    const [present, setPresent] = createSignal<ReadonlySet<string>>(new Set());
    this.present = present;
    this.#setPresent = setPresent;
    const [away, setAway] = createSignal<Record<string, number>>({});
    this.away = away;
    this.#setAway = setAway;
    const [announcement, setAnnouncement] = createSignal<{ id: string; from: string }>();
    this.announcement = announcement;
    this.#setAnnouncement = setAnnouncement;
    const [requests, setRequests] = createSignal<Record<string, string[]>>({});
    this.requests = requests;
    this.#setRequests = setRequests;
    const [requested, setRequested] = createSignal<Record<string, number>>({});
    this.requested = requested;
    this.#setRequested = setRequested;
    const [invitation, setInvitation] = createSignal<{ id: string; from: string }>();
    this.invitation = invitation;
    this.#setInvitation = setInvitation;
    const [declined, setDeclined] = createSignal<Record<string, number>>({});
    this.declined = declined;
    this.#setDeclined = setDeclined;
    const [open, setOpen] = createSignal(false);
    this.open = open;
    this.setOpen = setOpen;
    const [selected, select] = createSignal<string>();
    this.selected = selected;
    this.select = select;
  }

  get me() {
    return this.#me;
  }

  /**
   * The game this person is playing in the call now (theirs first), if any
   */
  playing(user: string) {
    const present = this.present();
    if (!present.has(user)) return;
    const games = Object.values(this.games()).filter(
      (game) => !finished(game) && canPlay(game, user) && present.has(game.host),
    );
    return games.find((game) => game.owner === user) ?? games[0];
  }

  /**
   * Ask the owner of a game to let us play
   */
  requestPlay(id: string) {
    const game = untrack(this.games)[id];
    if (!game || canPlay(game, this.#me) || !untrack(this.present).has(game.owner)) return;
    const at = untrack(this.requested)[id];
    if (at && Date.now() - at < REQUEST_AGAIN_MS) return;
    this.#setRequested({ ...untrack(this.requested), [id]: Date.now() });
    void this.#publish({ t: "request", id }, [game.owner]);
  }

  /**
   * The owner lets someone who asked play, or not
   */
  answer(id: string, user: string, ok: boolean) {
    const game = untrack(this.games)[id];
    this.#dropRequest(id, user);
    if (!game || game.owner !== this.#me) return;
    if (ok) this.act(id, { type: "allow", user, allow: true });
    void this.#publish({ t: "answer", id, ok }, [user]);
  }

  /**
   * Answer an invitation: play (opens the game) or not now (back to watching)
   */
  answerInvitation(play: boolean) {
    const invitation = untrack(this.invitation);
    this.#setInvitation(undefined);
    if (!invitation) return;
    if (play) {
      this.select(invitation.id);
      this.setOpen(true);
    } else {
      this.act(invitation.id, { type: "leave" });
    }
  }

  #dropRequest(id: string, user: string) {
    const requests = untrack(this.requests);
    if (!requests[id]?.includes(user)) return;
    const rest = { ...requests, [id]: requests[id].filter((u) => u !== user) };
    if (!rest[id].length) delete rest[id];
    this.#setRequests(rest);
  }

  /**
   * Follow the call we're in (undefined when we left it)
   */
  bind(room: Room | undefined, channel: string | undefined, client: () => Client) {
    this.#unbind?.();
    this.#unbind = undefined;
    if (!room || !channel) return;

    this.#room = room;
    this.#channel = channel;
    this.#client = client;
    this.#me = client().user?.id ?? "";

    const updatePresence = () =>
      this.#setPresent(
        new Set([this.#me, ...[...room.remoteParticipants.values()].map((p) => p.identity)]),
      );
    const onData = (
      payload: Uint8Array,
      participant?: Participant,
      _kind?: unknown,
      topic?: string,
    ) => {
      if (topic === TOPIC && participant) this.#receive(payload, participant.identity);
    };
    const onJoin = (participant: Participant) => {
      updatePresence();
      // newcomers get the games we run
      for (const game of Object.values(untrack(this.games))) {
        if (game.host === this.#me) this.#sendState(game, [participant.identity]);
      }
    };
    const onLeave = (participant: Participant) => {
      updatePresence();
      for (const id of Object.keys(untrack(this.requests))) {
        this.#dropRequest(id, participant.identity);
      }
      // their turn goes to the next player
      for (const game of Object.values(untrack(this.games))) {
        if (
          game.host === this.#me &&
          game.mode === "turns" &&
          game.turn === participant.identity &&
          !finished(game)
        ) {
          this.#commit(game, {
            ...game,
            turn: this.#nextTurn(game, participant.identity),
            turnAt: Date.now(),
          });
        }
      }
      const left = Object.values(untrack(this.games)).filter(
        (game) => game.host === participant.identity && !finished(game),
      );
      if (!left.length) return;
      const now = Date.now();
      const away = { ...untrack(this.away) };
      const games = { ...untrack(this.games) };
      for (const game of left) {
        away[game.id] = now;
        // nobody runs it: the clock stops where its host left
        const stopped = stopClock(game, now);
        games[game.id] = stopped;
        // keep what we know: it may be the last copy
        if (canPlay(game, this.#me)) this.#persist(stopped, true);
      }
      batch(() => {
        this.#setGames(games);
        this.#setAway(away);
      });
    };

    room.on(RoomEvent.DataReceived, onData);
    room.on(RoomEvent.ParticipantConnected, onJoin);
    room.on(RoomEvent.ParticipantDisconnected, onLeave);
    room.on(RoomEvent.Connected, updatePresence);
    updatePresence();
    this.#notesTimer = setInterval(() => void this.#flushNotes(), NOTES_SAVE_MS);
    void this.#loadSaved();

    this.#unbind = () => {
      room.off(RoomEvent.DataReceived, onData);
      room.off(RoomEvent.ParticipantConnected, onJoin);
      room.off(RoomEvent.ParticipantDisconnected, onLeave);
      room.off(RoomEvent.Connected, updatePresence);
      clearInterval(this.#notesTimer);
      for (const timer of this.#turnTimers.values()) clearTimeout(timer);
      this.#turnTimers.clear();
      // our games stop with us: save them as they are now
      for (const game of Object.values(untrack(this.games))) {
        if (!canPlay(game, this.#me) || finished(game)) continue;
        this.#persist(
          game.host === this.#me
            ? { ...game, board: setRunning(game.board, false) }
            : stopClock(game),
          true,
        );
      }
      batch(() => {
        this.#setGames({});
        this.#setSaved([]);
        this.#setAway({});
        this.#setPresent(new Set());
        this.#setAnnouncement(undefined);
        this.#setRequests({});
        this.#setRequested({});
        this.#setDeclined({});
        this.#setInvitation(undefined);
        this.setOpen(false);
        this.select(undefined);
      });
      this.#room = undefined;
      this.#channel = "";
    };
  }

  /**
   * Moves only while connected: a host cut off from the call would play alone
   */
  #connected() {
    const state = this.#room?.state;
    return !!this.#room && (state === undefined || state === ConnectionState.Connected);
  }

  /**
   * Start a new game run by us
   */
  create(
    difficulty: Difficulty,
    mode: GameMode = "free",
    players: string[] = [],
    rematchOf?: string,
  ) {
    if (!this.#room || !this.#me) return;
    const now = Date.now();
    const game: GameRecord = {
      id: ulid(),
      kind: "minesweeper",
      channel: this.#channel,
      owner: this.#me,
      players: players.slice(0, MAX_PLAYERS),
      host: this.#me,
      epoch: 0,
      seq: 1,
      paused: false,
      seed: crypto.getRandomValues(new Uint32Array(1))[0] & 0x7fffffff,
      mode,
      scores: {},
      turn: mode === "turns" ? this.#me : undefined,
      turnAt: mode === "turns" ? now : undefined,
      rematchOf,
      updatedAt: now,
      board: newMinesweeper(difficulty),
    };
    this.#upsert(game);
    this.#sendState(game);
    this.select(game.id);
    return game.id;
  }

  /**
   * A finished game again, same size and mode, with the same people playing
   * (anyone who played may start it); everyone watching moves to it
   */
  rematch(id: string) {
    const old = untrack(this.games)[id];
    if (!old || !finished(old) || !canPlay(old, this.#me)) return;
    const present = untrack(this.present);
    const players = [old.owner, ...old.players].filter(
      (user) => user !== this.#me && present.has(user),
    );
    const next = this.create(old.board.difficulty, old.mode, players, old.id);
    if (old.owner === this.#me) this.close(id);
    else this.#remove(id, false);
    return next;
  }

  /**
   * Turns: who plays after `after` (in order, skipping people not in the call)
   */
  #nextTurn(game: GameRecord, after: string) {
    const order = [game.owner, ...game.players];
    const present = untrack(this.present);
    const start = order.indexOf(after);
    for (let i = 1; i <= order.length; i++) {
      const user = order[(start + i + order.length) % order.length];
      if (present.has(user)) return user;
    }
    return after;
  }

  /**
   * Apply a change to a game we run and send it to the call
   */
  #commit(game: GameRecord, next: GameRecord, saveNow = false) {
    const committed = { ...next, seq: game.seq + 1, updatedAt: Date.now() };
    this.#upsert(committed, saveNow);
    this.#sendState(committed);
  }

  /**
   * Turns: in a game we run, the player on turn has TURN_MS to move
   */
  #scheduleTurn(game: GameRecord) {
    clearTimeout(this.#turnTimers.get(game.id));
    this.#turnTimers.delete(game.id);
    if (
      game.host !== this.#me ||
      game.mode !== "turns" ||
      !game.turn ||
      game.paused ||
      finished(game) ||
      game.turnAt === undefined
    ) {
      return;
    }
    const { turn, turnAt } = game;
    this.#turnTimers.set(
      game.id,
      setTimeout(
        () => {
          const current = untrack(this.games)[game.id];
          if (!current || current.turn !== turn || current.turnAt !== turnAt) return;
          if (current.host !== this.#me || current.paused || finished(current)) return;
          this.#commit(current, {
            ...current,
            turn: this.#nextTurn(current, turn),
            turnAt: Date.now(),
          });
        },
        Math.max(0, turnAt + TURN_MS - Date.now()),
      ),
    );
  }

  /**
   * Do something in a game: right away when we run it, else asked to its host
   */
  act(id: string, action: GameAction) {
    const game = untrack(this.games)[id];
    if (!game || !this.#connected()) return;
    if (game.host === this.#me) {
      this.#handle(game, action, this.#me);
    } else if (untrack(this.present).has(game.host)) {
      void this.#publish({ t: "ask", id, action }, [game.host]);
    }
  }

  /**
   * Run a game whose host is gone (or one of our saved games) from now on
   */
  takeOver(id: string) {
    const live = untrack(this.games)[id];
    const game = live ?? untrack(this.saved).find((g) => g.id === id);
    if (!game || !canPlay(game, this.#me) || finished(game) || !this.#connected()) return;
    // a saved game's host may be here without running it (other device)
    if (live && live.host !== this.#me && untrack(this.present).has(live.host)) return;

    const stopped = stopClock(game);
    const next: GameRecord = {
      ...stopped,
      host: this.#me,
      epoch: game.epoch + 1,
      seq: game.seq + 1,
      paused: false,
      updatedAt: Date.now(),
      board: setRunning(stopped.board, true),
    };
    if (next.mode === "turns") {
      if (!next.turn || !untrack(this.present).has(next.turn)) {
        next.turn = this.#nextTurn(next, next.turn ?? next.owner);
      }
      next.turnAt = Date.now();
    }
    const away = { ...untrack(this.away) };
    delete away[id];
    this.#setAway(away);
    this.#upsert(next, true);
    this.#sendState(next);
    this.select(id);
  }

  /**
   * End a game for everyone (owner only) and forget its saves
   */
  close(id: string) {
    const game =
      untrack(this.games)[id] ?? untrack(this.saved).find((g) => g.id === id);
    if (!game || game.owner !== this.#me) return;
    this.#markClosed(id);
    void this.#publish({ t: "close", id });
    this.#remove(id, true);
  }

  #markClosed(id: string) {
    this.#closed.add(id);
    writeStorage(CLOSED_KEY, [...this.#closed].slice(-CLOSED_MAX));
  }

  #handle(game: GameRecord, action: GameAction, from: string) {
    let next = game;
    if (action.type === "move") {
      if (!canPlay(game, from) || game.paused) return;
      // turns: only the player on turn moves (marks too)
      if (game.mode === "turns" && game.turn && from !== game.turn) return;
      const board = play(game.board, action.move, game.seed);
      if (board === game.board) return;
      const gained = opened(board) - opened(game.board);
      next = {
        ...game,
        board,
        scores:
          gained > 0
            ? { ...game.scores, [from]: (game.scores[from] ?? 0) + gained }
            : game.scores,
        explodedBy: board.status === "lost" ? from : game.explodedBy,
      };
      // turns: opening (not marking) passes the turn
      if (game.mode === "turns" && action.move.action !== "flag" && !finished(next)) {
        next = { ...next, turn: this.#nextTurn(next, from), turnAt: Date.now() };
      }
    } else if (action.type === "allow") {
      if (from !== game.owner || action.user === game.owner) return;
      // whoever runs the game keeps playing (else nobody would accept its states)
      if (!action.allow && action.user === game.host) return;
      if (action.allow && game.players.length >= MAX_PLAYERS) return;
      const players = action.allow
        ? [...new Set([...game.players, action.user])]
        : game.players.filter((p) => p !== action.user);
      next = { ...game, players };
      if (game.mode === "turns" && !action.allow && game.turn === action.user) {
        next = { ...next, turn: this.#nextTurn(game, action.user), turnAt: Date.now() };
      }
    } else if (action.type === "leave") {
      if (!canPlay(game, from)) return;
      // their score stays: they may come back while the game lasts
      const scores = { ...game.scores, [from]: game.scores[from] ?? 0 };
      if (from === game.owner) {
        // the game goes on with someone else in the call as its owner and host
        const present = untrack(this.present);
        const heir = game.players.find((p) => p !== from && present.has(p));
        if (!heir || from !== game.host) return;
        next = {
          ...game,
          owner: heir,
          host: heir,
          epoch: game.epoch + 1,
          players: game.players.filter((p) => p !== heir),
          scores,
        };
      } else {
        // whoever runs the game hands it to the owner first
        if (from === game.host) return;
        next = { ...game, players: game.players.filter((p) => p !== from), scores };
      }
      if (game.mode === "turns" && game.turn === from) {
        next = { ...next, turn: this.#nextTurn(game, from), turnAt: Date.now() };
      }
    } else if (action.type === "rejoin") {
      if (canPlay(game, from) || game.scores[from] === undefined || finished(game)) return;
      if (game.players.length >= MAX_PLAYERS) return;
      next = { ...game, players: [...game.players, from] };
    } else if (action.type === "pause") {
      if (!canPlay(game, from) || finished(game) || game.paused === action.paused) return;
      next = {
        ...game,
        paused: action.paused,
        board: setRunning(game.board, !action.paused),
        // the turn starts over after a pause
        turnAt: game.mode === "turns" && !action.paused ? Date.now() : game.turnAt,
      };
    }

    this.#commit(game, next, action.type === "pause" || finished(next));
  }

  #receive(payload: Uint8Array, from: string) {
    let message: Envelope;
    try {
      message = JSON.parse(new TextDecoder().decode(payload));
    } catch {
      return;
    }

    if (message?.t === "state" && typeof message.game === "string") {
      const game = decodeGame(message.game);
      if (!game || game.channel !== this.#channel) return;
      if (this.#closed.has(game.id)) return;
      const known =
        untrack(this.games)[game.id] ?? untrack(this.saved).find((g) => g.id === game.id);
      // the owner running the game handed it (and ownership) to a player
      const handoff =
        !!known &&
        from === known.owner &&
        from === known.host &&
        game.owner === game.host &&
        game.owner !== from &&
        known.players.includes(game.owner) &&
        game.epoch > known.epoch;
      if (game.host !== from && !handoff) return;
      // only someone allowed to play may run a game, and not change what it is
      if (!canPlay(known ?? game, from)) return;
      if (
        known &&
        ((game.owner !== known.owner && !handoff) ||
          game.seed !== known.seed ||
          game.board.difficulty !== known.board.difficulty ||
          !supersedes(game, known))
      ) {
        return;
      }
      // their clock to ours
      const offset = finiteOffset(message.now);
      const local: GameRecord = {
        ...game,
        updatedAt: game.updatedAt + offset,
        turnAt: game.turnAt === undefined ? undefined : game.turnAt + offset,
        board: {
          ...game.board,
          since: game.board.since === undefined ? undefined : game.board.since + offset,
        },
      };
      // a replay of a finished game: everyone on the old one moves to it
      if (game.rematchOf && !known) {
        const games = untrack(this.games);
        // two replays started at once: the smaller id stays
        const ours = Object.values(games).find(
          (g) => g.rematchOf === game.rematchOf && g.id !== game.id && g.owner === this.#me,
        );
        if (ours && ours.id < game.id) return;
        const selected = untrack(this.selected);
        if (ours) this.close(ours.id);
        const old = games[game.rematchOf];
        if (old && finished(old)) this.#remove(old.id, false);
        if (selected === game.rematchOf || (ours && selected === ours.id)) {
          this.select(game.id);
        }
      }
      const away = untrack(this.away);
      if (away[game.id]) {
        const rest = { ...away };
        delete rest[game.id];
        this.#setAway(rest);
      }
      this.#upsert(
        local,
        game.paused !== known?.paused || game.host !== known?.host || finished(game),
      );
      // a new game, just started by its owner
      if (!known && !game.rematchOf && game.owner === from && game.epoch === 0 && game.seq <= 3) {
        clearTimeout(this.#announceTimer);
        this.#setAnnouncement({ id: game.id, from });
        this.#announceTimer = setTimeout(
          () => this.#setAnnouncement(undefined),
          ANNOUNCE_MS,
        );
      }
      // let in without asking (and not a replay with the same people): invited
      if (
        known &&
        !canPlay(known, this.#me) &&
        canPlay(game, this.#me) &&
        !untrack(this.requested)[game.id] &&
        !finished(game)
      ) {
        this.#setInvitation({ id: game.id, from: game.owner });
      }
      if (untrack(this.invitation)?.id === game.id && !canPlay(game, this.#me)) {
        this.#setInvitation(undefined);
      }
      // allowed now: the request is answered
      if (canPlay(game, this.#me) && untrack(this.requested)[game.id]) {
        const rest = { ...untrack(this.requested) };
        delete rest[game.id];
        this.#setRequested(rest);
      }
    } else if (message?.t === "ask" && typeof message.id === "string") {
      const game = untrack(this.games)[message.id];
      const action = message.action;
      if (!game || game.host !== this.#me || !validAction(action)) return;
      this.#handle(game, action, from);
    } else if (message?.t === "request" && typeof message.id === "string") {
      const game = untrack(this.games)[message.id];
      if (!game || game.owner !== this.#me || finished(game) || canPlay(game, from)) return;
      if (!untrack(this.present).has(from)) return;
      const requests = untrack(this.requests);
      const list = requests[game.id] ?? [];
      if (list.includes(from) || list.length >= MAX_REQUESTS) return;
      this.#setRequests({ ...requests, [game.id]: [...list, from] });
    } else if (
      message?.t === "answer" &&
      typeof message.id === "string" &&
      typeof message.ok === "boolean"
    ) {
      const game = untrack(this.games)[message.id];
      if (!game || game.owner !== from) return;
      const rest = { ...untrack(this.requested) };
      delete rest[game.id];
      this.#setRequested(rest);
      if (!message.ok) {
        const at = Date.now();
        this.#setDeclined({ ...untrack(this.declined), [game.id]: at });
        setTimeout(() => {
          const declined = { ...untrack(this.declined) };
          if (declined[game.id] === at) delete declined[game.id];
          this.#setDeclined(declined);
        }, DECLINED_MS);
      }
    } else if (message?.t === "close" && typeof message.id === "string") {
      const game =
        untrack(this.games)[message.id] ??
        untrack(this.saved).find((g) => g.id === message.id);
      if (!game || game.owner !== from) return;
      this.#markClosed(game.id);
      this.#remove(game.id, canPlay(game, this.#me));
    }
  }

  #upsert(game: GameRecord, saveNow = false) {
    batch(() => {
      this.#setGames({ ...untrack(this.games), [game.id]: game });
      // people let in some other way don't need to ask any more
      for (const user of untrack(this.requests)[game.id] ?? []) {
        if (canPlay(game, user)) this.#dropRequest(game.id, user);
      }
      const saved = untrack(this.saved);
      if (saved.some((g) => g.id === game.id)) {
        this.#setSaved(saved.filter((g) => g.id !== game.id));
      }
    });
    if (canPlay(game, this.#me)) this.#persist(game, saveNow);
    this.#scheduleTurn(game);
  }

  #remove(id: string, forgetSaves: boolean) {
    clearTimeout(this.#turnTimers.get(id));
    this.#turnTimers.delete(id);
    batch(() => {
      const games = { ...untrack(this.games) };
      delete games[id];
      this.#setGames(games);
      this.#setSaved(untrack(this.saved).filter((g) => g.id !== id));
      if (untrack(this.selected) === id) this.select(undefined);
      const requests = { ...untrack(this.requests) };
      delete requests[id];
      this.#setRequests(requests);
      if (untrack(this.announcement)?.id === id) this.#setAnnouncement(undefined);
      if (untrack(this.invitation)?.id === id) this.#setInvitation(undefined);
    });
    if (forgetSaves) this.#forget(id);
  }

  async #publish(message: Envelope, to?: string[]) {
    const room = this.#room;
    if (!room) return;
    try {
      await room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify(message)),
        { reliable: true, topic: TOPIC, destinationIdentities: to },
      );
    } catch (err) {
      console.error("[konkord] game message not sent", err);
    }
  }

  #sendState(game: GameRecord, to?: string[]) {
    void this.#publish({ t: "state", game: encodeGame(game), now: Date.now() }, to);
  }

  // ---- saves: device + Saved Notes ----

  #persist(game: GameRecord, notesNow: boolean) {
    if (finished(game)) {
      // nothing to continue: drop the saves
      this.#forget(game.id);
      return;
    }
    this.#pendingLocal.set(game.id, game);
    clearTimeout(this.#localTimer);
    this.#localTimer = setTimeout(() => this.#flushLocal(), LOCAL_SAVE_MS);
    this.#dirty.set(game.id, game);
    if (notesNow) {
      this.#flushLocal();
      void this.#flushNotes();
    }
  }

  #flushLocal() {
    clearTimeout(this.#localTimer);
    if (!this.#pendingLocal.size) return;
    const saves = readLocal();
    for (const [id, game] of this.#pendingLocal) saves[id] = encodeGame(game);
    this.#pendingLocal.clear();
    writeStorage(LOCAL_KEY, prune(saves));
  }

  #forget(id: string) {
    this.#gone.add(id);
    this.#pendingLocal.delete(id);
    this.#dirty.delete(id);
    const saves = readLocal();
    if (id in saves) {
      delete saves[id];
      writeStorage(LOCAL_KEY, saves);
    }
    const noteId = this.#noteIds.get(id);
    if (noteId) {
      this.#noteIds.delete(id);
      void this.#deleteNotes([noteId]);
    }
  }

  #notes() {
    this.#notesChannel ??= (async () => {
      const client = this.#client?.();
      if (!client?.user) return;
      try {
        return (
          [...client.channels.values()].find((c) => c.type === "SavedMessages") ??
          (await client.user.openDM())
        );
      } catch (err) {
        console.error("[konkord] Saved Notes unavailable for games", err);
        this.#notesChannel = undefined;
      }
    })();
    return this.#notesChannel;
  }

  async #deleteNotes(ids: string[]) {
    const notes = await this.#notes();
    const client = this.#client?.();
    if (!notes || !client) return;
    for (const id of ids) {
      await client.api
        .delete(`/channels/${notes.id as ""}/messages/${id as ""}`)
        .catch(() => {});
    }
  }

  #flushNotes() {
    this.#writing = this.#writing.then(() => this.#writeNotes()).catch(() => {});
    return this.#writing;
  }

  async #writeNotes() {
    if (!this.#dirty.size) return;
    const notes = await this.#notes();
    const client = this.#client?.();
    if (!notes || !client) return;
    // the games as they were when marked: the call may be gone by now
    const games = [...this.#dirty.values()];
    this.#dirty.clear();
    for (const game of games) {
      if (this.#gone.has(game.id)) continue;
      const content = SAVE_PREFIX + encodeGame(game);
      const noteId = this.#noteIds.get(game.id);
      try {
        if (noteId) {
          try {
            await client.api.patch(
              `/channels/${notes.id as ""}/messages/${noteId as ""}`,
              { content },
            );
            continue;
          } catch {
            // deleted elsewhere: write a new one
            this.#noteIds.delete(game.id);
          }
        }
        const message = await notes.sendMessage(content);
        // ended while we were writing
        if (this.#gone.has(game.id)) {
          void this.#deleteNotes([message.id]);
          continue;
        }
        this.#noteIds.set(game.id, message.id);
      } catch (err) {
        console.error("[konkord] game not saved to Saved Notes", err);
        if (!this.#dirty.has(game.id)) this.#dirty.set(game.id, game);
      }
    }
  }

  /**
   * Our saved games of this channel, from the device and the Saved Notes
   */
  async #loadSaved() {
    const channel = this.#channel;
    const found = new Map<string, GameRecord>();
    const consider = (game?: GameRecord) => {
      if (!game || game.channel !== channel || finished(game)) return;
      if (this.#closed.has(game.id) || !canPlay(game, this.#me)) return;
      const known = found.get(game.id);
      if (!known || supersedes(game, known)) found.set(game.id, game);
    };

    for (const text of Object.values(readLocal())) {
      if (typeof text === "string") consider(decodeGame(text));
    }
    this.#publishSaved(found, channel);

    const notes = await this.#notes();
    if (!notes || this.#channel !== channel) return;
    try {
      // Stoat gives at most 100 per request: enough for the saves (one per game)
      const messages = await notes.fetchMessages({ limit: 100 });
      const byGame = new Map<string, { id: string; game: GameRecord }[]>();
      const leftovers: string[] = [];
      for (const message of messages) {
        if (message.authorId !== this.#me) continue;
        if (!message.content?.startsWith(SAVE_PREFIX)) continue;
        const game = decodeGame(message.content.slice(SAVE_PREFIX.length));
        // unreadable, ended or too old: not worth keeping
        if (
          !game ||
          this.#closed.has(game.id) ||
          finished(game) ||
          Date.now() - game.updatedAt > SAVED_MAX_AGE_MS
        ) {
          leftovers.push(message.id);
          continue;
        }
        byGame.set(game.id, [...(byGame.get(game.id) ?? []), { id: message.id, game }]);
        consider(game);
      }
      // one message per game: keep the newest, drop the rest
      for (const [gameId, copies] of byGame) {
        copies.sort((a, b) => (supersedes(a.game, b.game) ? -1 : 1));
        if (!this.#noteIds.has(gameId)) this.#noteIds.set(gameId, copies[0].id);
        leftovers.push(
          ...copies.map((c) => c.id).filter((id) => id !== this.#noteIds.get(gameId)),
        );
      }
      if (leftovers.length) void this.#deleteNotes(leftovers);
    } catch (err) {
      console.error("[konkord] could not read saved games", err);
    }
    this.#publishSaved(found, channel);
  }

  #publishSaved(found: Map<string, GameRecord>, channel: string) {
    if (this.#channel !== channel) return;
    const live = untrack(this.games);
    this.#setSaved(
      [...found.values()]
        .filter((game) => !live[game.id] && !this.#gone.has(game.id))
        .map((game) => stopClock(game))
        .sort((a, b) => b.updatedAt - a.updatedAt),
    );
  }
}

function finiteOffset(now: unknown) {
  if (typeof now !== "number" || !Number.isFinite(now)) return 0;
  const offset = Date.now() - now;
  // far off clocks: trust ours
  return Math.abs(offset) > 24 * 60 * 60_000 ? 0 : offset;
}

function validAction(action: unknown): action is GameAction {
  if (typeof action !== "object" || action === null) return false;
  const a = action as Record<string, unknown>;
  if (a.type === "move") {
    const move = a.move as Record<string, unknown> | null;
    return (
      typeof move === "object" &&
      move !== null &&
      ["open", "flag", "chord"].includes(move.action as string) &&
      Number.isInteger(move.cell)
    );
  }
  if (a.type === "allow") return typeof a.user === "string" && typeof a.allow === "boolean";
  if (a.type === "pause") return typeof a.paused === "boolean";
  if (a.type === "leave" || a.type === "rejoin") return true;
  return false;
}

/**
 * Keep the newest saves, drop old and finished ones
 */
function prune(saves: Record<string, string>) {
  const entries = Object.entries(saves)
    .map(([id, text]) => [id, text, decodeGame(text)] as const)
    .filter(
      ([, , game]) =>
        game && !finished(game) && Date.now() - game.updatedAt < SAVED_MAX_AGE_MS,
    )
    .sort((a, b) => b[2]!.updatedAt - a[2]!.updatedAt)
    .slice(0, SAVED_MAX);
  return Object.fromEntries(entries.map(([id, text]) => [id, text]));
}

export const callGames = new CallGames();
